<script>
    /**
     * @typedef {Object} Props
     * @property {boolean} [isLarge] - If true, use styling for larger size
     * @property {Object} [fragments]
     * Fragments is an object with values between 0 and 1 for each key:
     * green, orange, red, gray, blue, black
     * @property {Object} [labels] - labels of green, red and/or gray.
     * Optional labels to show as tooltips/title attributes for each fragment
     * By default it's assumed that the green, red and gray parts are used in the context
     * of a student's score. These can be overridden by passing different values for the
     * @property {Array} [markers]
     * Optional markers to indicate things such as median values
     * Should be an array with {label: String, value: Number} objects
     * @property {boolean} [showLabel] - If true, show value of the first fragment next to the bar
     * @property {boolean} [useAbsoluteValue]
     * If true, assume values should not be represented as percentages.
     * TIP: add a value to the fragments object that has a key that isn't included in colors
     * with the remainder value to calculate the correct total you want to show progress of.
     * For example when fragments: {blue: 120, remainder: 80}, the blue part will show up as 60% of the progress bar.
     * @property {boolean} [width] - Optional, width of the progress bar (without label) in pixels
     */

    /** @type {Props} */
    let {
        isLarge = false,
        fragments = $bindable({}),
        labels = {
            green: window.i18n.gettext('Correct'),
            red: window.i18n.gettext('Incorrect'),
            gray: window.i18n.gettext('Has not been graded yet'),
        },
        markers = [],
        showLabel = false,
        useAbsoluteValue = false,
        width = false
    } = $props()

    export function setFragments(newFragments) {
        fragments = newFragments
    }

    // List of valid color fragments
    const allColors = ['green', 'orange', 'red', 'gray', 'black', 'blue']

    // Colors visible in the progress bar
    const colors = $derived(
        allColors.filter(color => Number.isFinite(fragments[color]))
    )

    // Total of all values in the progress bar
    const sum = $derived(
        useAbsoluteValue
            ? Object.values(fragments).reduce((m, v) => m + v, 0)
            : 1
    )

    function getFragmentWidth(color) {
        return fragments[color] / sum * 100 + '%'
    }

    function getFragmentTitle(color) {
        if (useAbsoluteValue) {
            return fragments[color] + ' ' + (labels[color] || '')
        }

        return Math.round(fragments[color] * 100) + '% ' + (labels[color] || '')
    }

    function getMarkerTitle(marker) {
        if (useAbsoluteValue) {
            return marker.value + ' ' + marker.label
        }

        return Math.round(marker.value * 100) + '% ' + marker.label
    }

    function getMarkerPosition(marker) {
        return marker.value / sum * 100 + '%'
    }

    function setWidth(element) {
        if (width) {
            element.style.width = width + 'px'
        }
    }
</script>

<div class="bar-container">
    {#if showLabel}
        <span class="label" component="progress-bar__label">
            {#if colors.length === 1}
                {#if useAbsoluteValue}
                    {fragments[colors[0]]}
                {:else}
                    {Math.round(fragments[colors[0]] * 100) + '%'}
                {/if}
            {/if}
        </span>
    {/if}
    <div class="bar-outer" use:setWidth>
        <div class="progress-bar bar" class:is-large={isLarge}>
            {#each colors as color (color)}
                <div
                    class="fragment {color}"
                    style:width={getFragmentWidth(color)}
                    title={getFragmentTitle(color)}></div>
            {/each}
        </div>
        {#each markers as marker (marker.value)}
            <div class="marker" title={getMarkerTitle(marker)} style:left={getMarkerPosition(marker)}></div>
        {/each}
    </div>
</div>

<style lang="scss">/*
* Scut, a collection of Sass utilities
* to ease and improve our implementations of common style-code patterns.
* v1.0.1
* Docs at http://davidtheclark.github.io/scut
*/
/* TODO implement tooltip arrow */
.bar-container {
  display: flex;
  align-items: center;
}
.bar-container .label {
  font-size: 14px;
  display: block;
  min-width: 4ch;
  flex-shrink: 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  margin-right: 8px;
  text-align: right;
  cursor: inherit;
}
.bar-container .bar-outer {
  position: relative;
  width: 100%;
}
.bar-container .bar {
  height: 12px;
  background-color: rgba(17.6%, 48.4%, 70.4%, 0.1);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  width: inherit;
  min-width: 24px;
}
.bar-container .bar:not(:only-child) {
  margin: 4px 0;
}
.bar-container .bar .fragment {
  background-color: hsl(205, 60%, 44%);
  transition: all 0.2s cubic-bezier(0.455, 0.03, 0.515, 0.955);
  width: 0%;
}
.bar-container .bar .fragment:global(.green) {
  background-color: #3fd17d;
}
.bar-container .bar .fragment:global(.orange) {
  background-color: #ffa008;
}
.bar-container .bar .fragment:global(.red) {
  background-color: #fc7787;
}
.bar-container .bar .fragment:global(.gray) {
  background-color: #9a9bb5;
}
.bar-container .bar .fragment:global(.black) {
  background-color: #122d40;
}
.bar-container .bar.is-large {
  height: 24px;
  border-radius: 12px;
}
.bar-container .bar.is-large .fragment {
  transition-duration: 1s;
}
.bar-container .marker {
  position: absolute;
  width: 4px;
  top: 0;
  bottom: 0;
  transform: translateX(-50%);
  border-radius: 8px;
  background-color: #122d40;
}</style>
